import { Dtos } from "@inrev/common";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import {
	type SfaaClassExperienceRowsQuery,
	sfaaClassExperienceRowsQueryKey,
} from "../sfaa-class-experience.keys";

const appendValues = (
	params: URLSearchParams,
	key: string,
	values: readonly (number | string)[],
) => {
	for (const value of values) params.append(key, value.toString());
};

export const buildSfaaClassExperienceRowsSearchParams = (
	query: SfaaClassExperienceRowsQuery,
	offset: number,
) => {
	const params = new URLSearchParams({
		limit: Dtos.Admin.SfaaClassExperience.DEFAULT_ROW_LIMIT.toString(),
		offset: offset.toString(),
		sortField: query.sortField,
		sortDirection: query.sortDirection,
	});

	appendValues(params, "lines", query.lines ?? []);
	appendValues(params, "sublines", query.sublines ?? []);
	appendValues(params, "locations", query.locations ?? []);
	appendValues(params, "sfaaCodes", query.sfaaCodes ?? []);
	appendValues(params, "years", query.years ?? []);
	if (query.minYear !== undefined) params.set("minYear", query.minYear.toString());
	if (query.maxYear !== undefined) params.set("maxYear", query.maxYear.toString());
	if (query.search !== undefined) params.set("search", query.search);

	return params;
};

export const useFetchSfaaClassExperienceRows = (query: SfaaClassExperienceRowsQuery) => {
	const { get } = useRequest();

	return useInfiniteQuery({
		queryKey: sfaaClassExperienceRowsQueryKey(query),
		initialPageParam: 0,
		queryFn: async ({ pageParam }) => {
			const params = buildSfaaClassExperienceRowsSearchParams(query, pageParam);
			return await get<Dtos.Admin.SfaaClassExperience.List.Response>(
				`/v2/admin/surety/sfaa-class-experience/rows?${params.toString()}`,
			);
		},
		getNextPageParam: (lastPage) =>
			lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined,
	});
};
